import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  CARE_PLAN_REVIEW_SYNC_EVENT,
  type CarePlanReviewSyncDetail,
} from '@eluve/api-contract';
import { toast } from '@eluve/components';

import { useMiniAppApi } from '../../../hooks';
import type { MiniAppContext } from '../../../types';
import { CLINICAL_FINDINGS_AGENT_ID } from '../constants';
import { bumpFindings } from '../findings-refresh';

import { groupFindings } from './grouping';
import type { PendingFinding, Verdict } from './types';

export const FINDINGS_PREPARING_POLL_MS = 10_000;
export const FINDINGS_PREPARING_CAP_MS = 180_000;

export interface ReviewProgress {
  reviewed: number;
  total: number;
  approved: number;
  rejected: number;
}

type ReviewPhase = 'preparing' | 'review' | 'applied' | 'empty';

const progressOf = (findings: PendingFinding[]): ReviewProgress => {
  let approved = 0;
  let rejected = 0;
  for (const f of findings) {
    if (f.decision === 'approved' || f.decision === 'edited') approved += 1;
    else if (f.decision === 'rejected') rejected += 1;
  }
  return {
    reviewed: approved + rejected,
    total: findings.length,
    approved,
    rejected,
  };
};

export const useFindingsReview = (context: MiniAppContext) => {
  const { tenantId, appointmentId } = context;
  const api = useMiniAppApi(tenantId, CLINICAL_FINDINGS_AGENT_ID);

  const [findings, setFindings] = useState<PendingFinding[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [capReached, setCapReached] = useState(false);
  const [hasSeenFindings, setHasSeenFindings] = useState(false);
  const [applied, setApplied] = useState(false);
  const [mutating, setMutating] = useState(0);
  const [rerunning, setRerunning] = useState(false);
  const foundRef = useRef(false);
  const loadGenerationRef = useRef(0);

  const load = useCallback(
    async (throwOnError = false) => {
      if (!appointmentId) return;
      const generation = loadGenerationRef.current;
      try {
        const res = await api.listFindings(appointmentId);
        if (generation !== loadGenerationRef.current) return;
        if (res.status === 200) {
          setFindings(
            res.body.findings.map((finding) => ({
              ...finding,
              decision: finding.decision ?? 'pending',
            })),
          );
          if (res.body.findings.length > 0 && !foundRef.current) {
            foundRef.current = true;
            setHasSeenFindings(true);
            // Extraction just finished for this session. A care-plan tab
            // mounted alongside us drafted before findings existed, so nudge it
            // to refetch — otherwise its findings-derived goals and measures
            // only appear on a manual reload. Guarded on the first-discovery
            // transition, so this fires once, not on every poll.
            window.dispatchEvent(
              new CustomEvent<CarePlanReviewSyncDetail>(
                CARE_PLAN_REVIEW_SYNC_EVENT,
                { detail: { tenantId, appointmentId, source: 'findings' } },
              ),
            );
          }
        }
      } catch (error) {
        if (throwOnError) throw error;
      } finally {
        if (generation === loadGenerationRef.current) setLoaded(true);
      }
    },
    [api, appointmentId],
  );

  useEffect(() => {
    loadGenerationRef.current += 1;
    setFindings([]);
    setApplied(false);
    setCapReached(false);
    setHasSeenFindings(false);
    if (!appointmentId) {
      setLoaded(true);
      return;
    }
    foundRef.current = false;
    setLoaded(false);
    let cancelled = false;

    void load();
    const interval = setInterval(() => {
      if (cancelled) return;
      if (foundRef.current) {
        clearInterval(interval);
        return;
      }
      void load();
    }, FINDINGS_PREPARING_POLL_MS);
    const cap = setTimeout(() => {
      if (!foundRef.current) setCapReached(true);
      clearInterval(interval);
    }, FINDINGS_PREPARING_CAP_MS);

    return () => {
      loadGenerationRef.current += 1;
      cancelled = true;
      clearInterval(interval);
      clearTimeout(cap);
    };
  }, [appointmentId, load]);

  useEffect(() => {
    const syncFromCarePlan = (event: Event) => {
      const detail = (event as CustomEvent<CarePlanReviewSyncDetail>).detail;
      if (
        detail?.source === 'care-plan' &&
        detail.tenantId === tenantId &&
        detail.appointmentId === appointmentId
      ) {
        void load();
      }
    };
    window.addEventListener(CARE_PLAN_REVIEW_SYNC_EVENT, syncFromCarePlan);
    return () =>
      window.removeEventListener(CARE_PLAN_REVIEW_SYNC_EVENT, syncFromCarePlan);
  }, [appointmentId, load, tenantId]);

  const runWrite = useCallback(
    async (label: string, write: () => Promise<{ status: number }>) => {
      if (!appointmentId) return;
      const generation = loadGenerationRef.current;
      setMutating((n) => n + 1);
      try {
        const res = await write();
        if (generation !== loadGenerationRef.current) {
          return res.status === 200;
        }
        if (res.status !== 200) {
          toast.error(`Could not ${label}.`);
          return false;
        }
        await load(true);
        bumpFindings();
        window.dispatchEvent(
          new CustomEvent<CarePlanReviewSyncDetail>(
            CARE_PLAN_REVIEW_SYNC_EVENT,
            {
              detail: { tenantId, appointmentId, source: 'findings' },
            },
          ),
        );
        return true;
      } catch {
        if (generation === loadGenerationRef.current) {
          toast.error(`Could not ${label}.`);
        }
        return false;
      } finally {
        setMutating((n) => n - 1);
      }
    },
    [appointmentId, load, tenantId],
  );

  const decide = useCallback(
    (finding: PendingFinding, verdict: Verdict) =>
      runWrite(
        verdict === 'approve' ? 'approve finding' : 'reject finding',
        () =>
          api.review(appointmentId!, [
            {
              resourceType: finding.resourceType,
              id: finding.id,
              decision: verdict,
            },
          ]),
      ),
    [api, appointmentId, runWrite],
  );

  const undo = useCallback(
    (finding: PendingFinding) =>
      runWrite('undo', () =>
        api.revert(appointmentId!, [
          { resourceType: finding.resourceType, id: finding.id },
        ]),
      ),
    [api, appointmentId, runWrite],
  );

  const dismiss = useCallback(
    (finding: PendingFinding) =>
      runWrite('delete finding', () =>
        api.dismissFinding(appointmentId!, {
          resourceType: finding.resourceType,
          id: finding.id,
        }),
      ),
    [api, appointmentId, runWrite],
  );

  const approveAll = useCallback(
    (group: PendingFinding[]) => {
      const pending = group.filter((f) => f.decision === 'pending');
      if (pending.length === 0) return;
      return runWrite('approve findings', () =>
        api.review(
          appointmentId!,
          pending.map((f) => ({
            resourceType: f.resourceType,
            id: f.id,
            decision: 'approve' as const,
          })),
        ),
      );
    },
    [api, appointmentId, runWrite],
  );

  const rerun = useCallback(async () => {
    if (!appointmentId) return;
    const generation = loadGenerationRef.current;
    setRerunning(true);
    try {
      const res = await api.run({ appointmentId });
      if (generation !== loadGenerationRef.current) return;
      if (res.status === 200) {
        const { outcome, factCount } = res.body;
        if (outcome === 'extracted') {
          toast.success(
            `Re-extracted ${factCount} finding${factCount === 1 ? '' : 's'}.`,
          );
        } else if (outcome === 'empty') {
          toast.warning('No new findings were found in this session.');
        } else {
          toast.warning('Nothing to extract for this session.');
        }
        await load(true);
        bumpFindings();
      } else {
        toast.error('Could not re-run the extraction.');
      }
    } catch {
      if (generation === loadGenerationRef.current) {
        toast.error('Could not re-run the extraction.');
      }
    } finally {
      setRerunning(false);
    }
  }, [api, appointmentId, load]);

  const groups = useMemo(() => groupFindings(findings), [findings]);
  const progress = useMemo(() => progressOf(findings), [findings]);
  const allReviewed =
    progress.total > 0 && progress.reviewed === progress.total;

  const phase: ReviewPhase = findings.length
    ? applied
      ? 'applied'
      : 'review'
    : loaded && (capReached || hasSeenFindings)
      ? 'empty'
      : 'preparing';

  return {
    phase,
    apply: () => setApplied(true),
    allReviewed,
    mutating: mutating > 0,
    rerunning,
    findings,
    groups,
    progress,
    decide,
    undo,
    dismiss,
    approveAll,
    rerun,
    reload: load,
  };
};
